<script setup lang="ts">
	import {
		BarElement,
		CategoryScale,
		Chart as ChartJS,
		Legend,
		LinearScale,
		Title,
		Tooltip,
	} from 'chart.js'
	import { computed } from 'vue'
	import { Bar } from 'vue-chartjs'

	import useCompanyReportHotelAnalytics from '@/modules/companies/composables/useCompanyReportHotelAnalytics'

	interface Props {
		companyId?: number
		dateRange?: string[] | null
		creationDateRange?: string[] | null
		statusFilter?: string
		groupByFilter?: string
		searchTravelerId?: number | null
		searchHotelId?: number | null
		createdAt?: string[] | null
		orderBy?: string
		orderDirection?: 'asc' | 'desc'
	}

	const props = defineProps<Props>()

	// Hacer petición autónoma
	const { analytics, isFetching: isLoading } = useCompanyReportHotelAnalytics(
		computed(() => ({
			companyId: props.companyId,
			filters: {
				dates: props.dateRange,
				creationDates: props.creationDateRange,
				status: props.statusFilter !== 'all' ? props.statusFilter : undefined,
				groupBy: props.groupByFilter,
				travelerId: props.searchTravelerId || undefined,
				hotelId: props.searchHotelId || undefined,
				createdAt: props.createdAt || undefined,
				orderBy: props.orderBy || undefined,
				orderDirection: props.orderDirection || undefined,
			},
		}))
	)

	ChartJS.register(
		Title,
		Tooltip,
		Legend,
		BarElement,
		CategoryScale,
		LinearScale
	)

	const chartData = computed(() => {
		if (
			!analytics.value ||
			!analytics.value.topDestinations ||
			analytics.value.topDestinations.length === 0
		)
			return null

		return {
			labels: analytics.value.topDestinations.map((d: any) => d.city),
			datasets: [
				{
					label: 'Reservas',
					data: analytics.value.topDestinations.map((d: any) => d.reservations),
					backgroundColor: 'rgba(59, 130, 246, 0.8)',
				},
			],
		}
	})

	const chartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: {
				display: false,
			},
			title: {
				display: true,
				text: 'Top 10 Destinos más Solicitados',
			},
			datalabels: {
				display: false,
			},
		},
		scales: {
			y: {
				beginAtZero: true,
			},
		},
	}

	// Exponer isLoading para el componente padre
	defineExpose({
		isLoading,
	})
</script>

<template>
	<div class="overflow-hidden rounded-lg bg-white p-6 shadow">
		<div
			v-if="isLoading && !chartData"
			class="flex items-center justify-center"
			style="height: 300px"
		>
			<div class="text-center">
				<span
					class="material-symbols-outlined mb-2 animate-spin text-4xl text-blue-ef-500"
				>
					progress_activity
				</span>
				<p class="text-sm text-gray-600">Cargando destinos...</p>
			</div>
		</div>

		<div
			v-else-if="chartData"
			style="height: 300px"
		>
			<Bar
				:data="chartData"
				:options="chartOptions"
			/>
		</div>

		<div
			v-else
			class="flex items-center justify-center"
			style="height: 300px"
		>
			<div class="text-center">
				<span class="material-symbols-outlined mb-4 text-6xl text-gray-300">
					location_on
				</span>
				<p class="text-base font-medium text-gray-600">
					No hay datos disponibles para esta sección
				</p>
				<p class="mt-2 text-sm text-gray-500">
					No se encontraron destinos en el período seleccionado
				</p>
			</div>
		</div>
	</div>
</template>
